// Maps lowercased entity names (as they appear in news_articles.extracted_entities
// and arc.key_entities) to ISO-3166-1 alpha-2 country codes.
//
// Used by the news alert system to auto-detect which countries an article
// is relevant to. Editors can override via target_countries at publish time.
//
// Keys must be lowercased and trimmed. Multiple aliases → same code.
// All 54 African nations included. Additional nations commonly covered by
// Global Village editorial (US/UK/France allies and antagonists, etc.).

export const ENTITY_TO_COUNTRY: Record<string, string> = {
  // ── Africa (54 nations) ────────────────────────────────────
  "algeria": "DZ",
  "angola": "AO",
  "benin": "BJ",
  "botswana": "BW",
  "burkina faso": "BF",
  "burkinabé": "BF",
  "burkinabe": "BF",
  "burundi": "BI",
  "cabo verde": "CV",
  "cape verde": "CV",
  "cameroon": "CM",
  "central african republic": "CF",
  "car": "CF",
  "chad": "TD",
  "comoros": "KM",
  "republic of the congo": "CG",
  "congo-brazzaville": "CG",
  "democratic republic of the congo": "CD",
  "dr congo": "CD",
  "drc": "CD",
  "congo-kinshasa": "CD",
  "côte d'ivoire": "CI",
  "cote d'ivoire": "CI",
  "ivory coast": "CI",
  "djibouti": "DJ",
  "egypt": "EG",
  "equatorial guinea": "GQ",
  "eritrea": "ER",
  "eswatini": "SZ",
  "swaziland": "SZ",
  "ethiopia": "ET",
  "ethiopian": "ET",
  "gabon": "GA",
  "gambia": "GM",
  "the gambia": "GM",
  "ghana": "GH",
  "ghanaian": "GH",
  "guinea": "GN",
  "guinea-bissau": "GW",
  "kenya": "KE",
  "kenyan": "KE",
  "lesotho": "LS",
  "liberia": "LR",
  "libya": "LY",
  "madagascar": "MG",
  "malawi": "MW",
  "mali": "ML",
  "mauritania": "MR",
  "mauritius": "MU",
  "morocco": "MA",
  "moroccan": "MA",
  "mozambique": "MZ",
  "namibia": "NA",
  "niger": "NE",
  "nigeria": "NG",
  "nigerian": "NG",
  "rwanda": "RW",
  "são tomé and príncipe": "ST",
  "sao tome and principe": "ST",
  "senegal": "SN",
  "senegalese": "SN",
  "seychelles": "SC",
  "sierra leone": "SL",
  "somalia": "SO",
  "somali": "SO",
  "south africa": "ZA",
  "south african": "ZA",
  "south sudan": "SS",
  "sudan": "SD",
  "sudanese": "SD",
  "tanzania": "TZ",
  "togo": "TG",
  "tunisia": "TN",
  "uganda": "UG",
  "zambia": "ZM",
  "zimbabwe": "ZW",

  // ── Commonly referenced non-African nations ────────────────
  "united states": "US",
  "united states of america": "US",
  "usa": "US",
  "us": "US",
  "america": "US",
  "american": "US",
  "united kingdom": "GB",
  "uk": "GB",
  "britain": "GB",
  "british": "GB",
  "france": "FR",
  "french": "FR",
  "germany": "DE",
  "german": "DE",
  "china": "CN",
  "chinese": "CN",
  "russia": "RU",
  "russian": "RU",
  "iran": "IR",
  "iranian": "IR",
  "iraq": "IQ",
  "iraqi": "IQ",
  "israel": "IL",
  "israeli": "IL",
  "palestine": "PS",
  "palestinian": "PS",
  "saudi arabia": "SA",
  "saudi": "SA",
  "united arab emirates": "AE",
  "uae": "AE",
  "qatar": "QA",
  "yemen": "YE",
  "yemeni": "YE",
  "pakistan": "PK",
  "india": "IN",
  "turkey": "TR",
  "türkiye": "TR",
  "venezuela": "VE",
  "venezuelan": "VE",
  "cuba": "CU",
  "cuban": "CU",
  "brazil": "BR",
  "brazilian": "BR",
  "canada": "CA",
  "haiti": "HT",
  "haitian": "HT",
  "syria": "SY",
  "syrian": "SY",
  "lebanon": "LB",
  "lebanese": "LB",
  "jordan": "JO",
  "japan": "JP",
  "japanese": "JP",
  "south korea": "KR",
  "north korea": "KP",
  "afghanistan": "AF",
  "afghan": "AF",
  "ukraine": "UA",
  "ukrainian": "UA",
  "australia": "AU",
  "spain": "ES",
  "spanish": "ES",
  "italy": "IT",
  "italian": "IT",
  "portugal": "PT",
  "portuguese": "PT",
  "netherlands": "NL",
  "dutch": "NL",
  "belgium": "BE",
  "belgian": "BE",
  "greece": "GR",
  "mexico": "MX",
  "mexican": "MX",
};

/**
 * Detect target country codes from an article's extracted entities.
 * Case-insensitive, whitespace-tolerant, de-duplicated.
 */
export function detectCountriesFromEntities(entities: string[]): string[] {
  const found = new Set<string>();
  for (const entity of entities) {
    const key = entity.toLowerCase().trim();
    const code = ENTITY_TO_COUNTRY[key];
    if (code) found.add(code);
  }
  return Array.from(found).sort();
}

// ── Reverse lookup: ISO code → canonical display name ──────────────
// Used by the admin broadcast composer to render a country picker.
// Grouped so the UI can show Africa first (the editorial focus).

export const AFRICAN_COUNTRIES: Array<{ code: string; name: string }> = [
  { code: "DZ", name: "Algeria" },
  { code: "AO", name: "Angola" },
  { code: "BJ", name: "Benin" },
  { code: "BW", name: "Botswana" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cabo Verde" },
  { code: "CM", name: "Cameroon" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo (Brazzaville)" },
  { code: "CD", name: "DR Congo" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "DJ", name: "Djibouti" },
  { code: "EG", name: "Egypt" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GH", name: "Ghana" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "KE", name: "Kenya" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "ML", name: "Mali" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "RW", name: "Rwanda" },
  { code: "ST", name: "São Tomé and Príncipe" },
  { code: "SN", name: "Senegal" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "SS", name: "South Sudan" },
  { code: "SD", name: "Sudan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TG", name: "Togo" },
  { code: "TN", name: "Tunisia" },
  { code: "UG", name: "Uganda" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
];

export const OTHER_COUNTRIES: Array<{ code: string; name: string }> = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "CN", name: "China" },
  { code: "RU", name: "Russia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IL", name: "Israel" },
  { code: "PS", name: "Palestine" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "UAE" },
  { code: "QA", name: "Qatar" },
  { code: "YE", name: "Yemen" },
  { code: "PK", name: "Pakistan" },
  { code: "IN", name: "India" },
  { code: "TR", name: "Turkey" },
  { code: "VE", name: "Venezuela" },
  { code: "CU", name: "Cuba" },
  { code: "BR", name: "Brazil" },
  { code: "CA", name: "Canada" },
  { code: "HT", name: "Haiti" },
  { code: "SY", name: "Syria" },
  { code: "LB", name: "Lebanon" },
  { code: "JO", name: "Jordan" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "KP", name: "North Korea" },
  { code: "AF", name: "Afghanistan" },
  { code: "UA", name: "Ukraine" },
  { code: "AU", name: "Australia" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "PT", name: "Portugal" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "GR", name: "Greece" },
  { code: "MX", name: "Mexico" },
];

export const COUNTRY_CODE_TO_NAME: Record<string, string> = Object.fromEntries(
  [...AFRICAN_COUNTRIES, ...OTHER_COUNTRIES].map((c) => [c.code, c.name]),
);
